import { getAppConfigValue } from "@elliemae/pui-app-sdk";
import { initLogRocket } from "@elliemae/pui-logrocket";
import { randomUUID } from "@frontend/utils";
const getLogRocketInstance = () => window.emui?.logRocket;
function resolveLogRocketAppId(appId, launchContext) {
  if (!appId || typeof appId === "string") return appId;
  return launchContext === "tpo" ? appId.tpo ?? appId.encw : appId.encw;
}
function initializeLogRocket(config, launchContext) {
  const appId = resolveLogRocketAppId(
    config?.appId ?? getAppConfigValue("logRocketConfig.appId"),
    launchContext
  );
  const domainOptions = launchContext ? {} : {
    parentDomain: config?.parentDomain ?? getAppConfigValue("logRocketConfig.parentDomain"),
    rootHostname: config?.rootHostname ?? getAppConfigValue("logRocketConfig.rootHostname"),
    childDomains: config?.childDomains ?? getAppConfigValue("logRocketConfig.childDomains")
  };
  window.process = window.process || { env: {} };
  initLogRocket({
    appId,
    ...domainOptions,
    network: {
      requestSanitizer: sanitizeRequest,
      responseSanitizer: sanitizeResponse
    }
  });
  if (launchContext !== "encw" && launchContext !== "tpo") {
    setTimeout(() => {
      getLogRocketInstance()?.startNewSession?.();
      captureAppSessionLink();
    }, 0);
  } else {
    captureAppSessionLink();
  }
  installCorrelationIdInterceptor();
}
function identifyLogRocketUser(traits, userId) {
  const id = userId ?? randomUUID();
  getLogRocketInstance()?.identify(id, traits);
}
function trackLogRocketEvent(event, properties) {
  try {
    getLogRocketInstance()?.track(event, properties);
  } catch {
  }
}
const SENSITIVE_HEADERS = [
  "authorization",
  "cookie",
  "set-cookie",
  "x-opaque-token",
  "x-elli-pat"
];
const SENSITIVE_BODY_KEYS = [
  // borrower names
  "firstName",
  "lastName",
  "firstNameWithMiddleName",
  "lastNameWithSuffix",
  // contact
  "emailAddressText",
  "workEmailAddress",
  "email",
  "homePhoneNumber",
  // address
  "mailingAddress",
  "addressCity",
  "addressState",
  "addressPostalCode",
  "addressStreetLine1",
  "streetAddress",
  "streetAddressLine1",
  "streetAddressLine2",
  "subjectPropertyStreetAddress",
  "subjectPropertyPropertyStreetAddress",
  "urla2020StreetAddress",
  // credit / financial
  "creditScore",
  "creditScoreToUse",
  "ausAverageMedianCreditScore",
  "totalMonthlyIncome",
  "totalMonthlyIncomeMinusNetRentalAmount",
  "assets",
  "liabilities",
  "assetsAvailableAmount",
  // HMDA object - contains race / ethnicity / sex
  "hmda"
];
function redactHeaders(headers) {
  if (!headers) return;
  for (const key of Object.keys(headers)) {
    if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
      headers[key] = "";
    }
  }
}
function redactBodyKeys(value) {
  if (Array.isArray(value)) return value.map(redactBodyKeys);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = SENSITIVE_BODY_KEYS.includes(k) ? "[REDACTED]" : redactBodyKeys(v);
    }
    return out;
  }
  return value;
}
function sanitizeBody(body) {
  if (!body) return body;
  try {
    return JSON.stringify(redactBodyKeys(JSON.parse(body)));
  } catch {
    return null;
  }
}
function sanitizeRequest(request) {
  redactHeaders(request.headers);
  request.body = sanitizeBody(request.body);
  return request;
}
function sanitizeResponse(response) {
  if (response.headers?.["x-secret"]) return null;
  redactHeaders(response.headers);
  response.body = sanitizeBody(response.body);
  return response;
}
const KPI = {
  InitialPartnerUiLoad: "InitialPartnerUiLoad",
  Qualification: "Qualification",
  SelectRate: "SelectRate",
  RequestLock: "RequestLock",
  AcceptLock: "AcceptLock",
  SelectBuySellSide: "SelectBuySellSide",
  DenyLock: "DenyLock"
};
function getBuildStamp() {
  try {
    const v = document.querySelector('meta[name="build-date"]')?.getAttribute("content");
    return v ? decodeURIComponent(v) : void 0;
  } catch {
    return void 0;
  }
}
let cachedReleaseVersion;
function getReleaseVersion() {
  if (cachedReleaseVersion !== void 0) return cachedReleaseVersion;
  const stamp = getBuildStamp();
  const branch = stamp?.match(/frontend-(.+)-\d+-[0-9a-f]{7,40}$/i)?.[1];
  const version = branch ? branch.replace(/^release\//, "") : window.emui?.version ?? "latest";
  cachedReleaseVersion = version;
  return version;
}
function trackKpi(transaction, mark, context = {}) {
  try {
    getLogRocketInstance()?.track(`${transaction}_${mark}`, {
      businessTransaction: transaction,
      productLine: "NG",
      releaseVersion: getReleaseVersion(),
      ...mark === "End" ? { correlationId: getLastCorrelationId() } : {},
      ...context
    });
  } catch {
  }
}
function trackKpiStart(transaction, context) {
  trackKpi(transaction, "Start", context);
}
function trackKpiEnd(transaction, context) {
  trackKpi(transaction, "End", context);
}
const APP_SESSION_LINK_HEADER = "X-ICE-AppSessionLink";
let appSessionLink;
function getAppSessionLink() {
  return appSessionLink;
}
function captureAppSessionLink() {
  try {
    getLogRocketInstance()?.getSessionURL?.((url) => {
      appSessionLink = url;
    });
  } catch {
  }
}
const CORRELATION_ID_HEADER = "X-Correlation-Id";
let lastCorrelationId;
function getLastCorrelationId() {
  return lastCorrelationId;
}
function installCorrelationIdInterceptor() {
  if (typeof window === "undefined" || !window.fetch) return;
  if (window.__logRocketCorrelationPatched) return;
  window.__logRocketCorrelationPatched = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    try {
      const correlationId = response.headers.get(CORRELATION_ID_HEADER);
      if (correlationId) {
        lastCorrelationId = correlationId;
        const method = (input instanceof Request ? input.method : init?.method) ?? "GET";
        getLogRocketInstance()?.track("otel-correlation", {
          correlationId,
          method: method.toUpperCase(),
          url: response.url,
          status: response.status
        });
      }
    } catch {
    }
    return response;
  };
}
export {
  APP_SESSION_LINK_HEADER,
  KPI,
  getAppSessionLink,
  getLastCorrelationId,
  identifyLogRocketUser,
  initializeLogRocket,
  resolveLogRocketAppId,
  trackKpiEnd,
  trackKpiStart,
  trackLogRocketEvent
};
